import { Camera, Search } from 'lucide-react';
import { FOKUS_RING } from '@/lib/fokus';

type Props = {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  /**
   * Öffnet das Scan-Fenster im Suchmodus.
   *
   * Das Fenster selbst hängt seit E4 an der Seite, nicht mehr hier: es
   * gibt zwei Einstiege (Suchen und Wareneingang), und es darf nur EINE
   * Kamera geben. Was ein Code bedeutet, entscheidet weiterhin die Seite.
   */
  onScanOeffnen: () => void;
  isLoading?: boolean;
  /**
   * Auf schmalen Geräten steht das Feld allein in seiner Zeile und darf
   * sie ganz ausfüllen. Die Begrenzung auf 28rem ist eine Lesbarkeits-
   * grenze für breite Bildschirme, kein Wert an sich.
   */
  vollBreite?: boolean;
  /**
   * Kurzer Tastenhinweis rechts im Feld („Strg K"), nur in der oberen
   * Leiste am PC und nur, solange das Feld leer ist.
   */
  tastenHinweis?: string;
  /** Platzhalter; die obere Leiste ist schmaler und sagt es kürzer. */
  platzhalter?: string;
};

export function DashboardSearchInput({
  value,
  onChange,
  onSubmit,
  onScanOeffnen,
  isLoading,
  vollBreite,
  tastenHinweis,
  platzhalter = 'Suche nach Kunde, Bestellnr, ISBN, Titel …'
}: Props) {
  return (
    <div
      className={vollBreite ? 'relative w-full' : 'relative flex-1 max-w-md'}
      data-tour="dashboard-suche"
    >
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint" />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSubmit();
          }
        }}
        placeholder={platzhalter}
        aria-label="Suche nach Kunde, Bestellnummer, ISBN oder Titel"
        // py-3 statt py-2.5: das Feld trägt jetzt einen Knopf mit 44 Pixeln
        // Kantenlänge (CLAUDE.md, Touch-Ziele). Bei der alten Höhe stünde
        // der über den Rand hinaus. Nebenbei wird auch das Feld selbst zum
        // ordentlichen Touch-Ziel.
        className={`min-h-[44px] w-full rounded-md border border-line bg-cream py-3 pl-10 pr-14 text-sm ${tastenHinweis ? 'lg:pr-28' : ''} ${FOKUS_RING}`}
        aria-busy={isLoading ? true : undefined}
      />
      {/* Der Scan-Knopf gehört ins Suchfeld, nicht daneben: gescannt wird,
          um etwas zu finden — es ist dieselbe Handlung wie tippen, nur
          schneller. */}
      <button
        data-nutzung="scan_kamera"
        type="button"
        data-tour="scan-button"
        onClick={onScanOeffnen}
        aria-label="Mit der Kamera scannen"
        title="QR vom Zettel oder Buch-Barcode scannen"
        className={`absolute right-1 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-md text-ink-soft hover:bg-cream-dark hover:text-akzent ${FOKUS_RING}`}
      >
        <Camera className="h-5 w-5" />
      </button>
      {tastenHinweis && value === '' && (
        <kbd
          aria-hidden
          className="pointer-events-none absolute right-12 top-1/2 hidden -translate-y-1/2 rounded border border-line px-1 text-[10px] text-ink-faint lg:block"
        >
          {tastenHinweis}
        </kbd>
      )}

    </div>
  );
}
